import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";
import Page from "components/page/Page";
import { useAppSelector } from "hooks/redux";

import { StatusCode } from "api/protocols";
import Input from "components/form/input/Input";
import Card from "components/card/Card";
import { Select } from "components/select/index";
import { useTranslation } from "react-i18next";
import Popup from "components/popup/Popup";
import { isEqualWith } from "lodash";
import {
  DeleteDepartments,
  GetDepartments,
  PutDepartments,
  UpdateAllDepartmentProducts,
} from "api/rpc/2024-04/facilityAdmin/facility/department";
import { DepartmentType, TProductType, TProductVendor } from "redux/reducers/models/product";
import Toggle from "components/form/toggle/Toggle";
import { TAccountingReference } from "redux/reducers/models/facility";
import { showError } from "redux/actions/ui";
import { useDispatch } from "react-redux";
import axios, { CancelToken } from "axios";
import { GetVendor } from "api/rpc/2024-04/facilityAdmin/product/vendor";
import { GetProductTypes } from "api/rpc/2024-04/facilityAdmin/product/type";
import { GetAccountingReference } from "api/rpc/2024-04/facilityAdmin/facility/accounting";
import DataTable from "components/dataTable/DataTable";
import Checkbox from "components/form/checkbox/Checkbox";
import { TKitchenLocation } from "redux/reducers/models/kitchen";
import { GetKitchenLocations } from "api/rpc/2024-04/facilityAdmin/facility/kitchen/location";

interface IDepartmentState {
  id: number;
  title: string;
  type: DepartmentType;
  parent_id: number;

  //Update All Products
  productType?: string;
  vendor?: any | null;
  vendor_id?: any | null;
  accounting_reference?: any;
  accounting_reference_id?: string | number;
  kitchen_location?: any;
  kitchen_location_id?: number;
  kitchen_location_id_2?: number;
  kitchen_location_id_3?: number;
  customer_required?: boolean;
  fulfillment_required?: boolean;
  shipping_required?: boolean;
  track_inventory?: boolean;
  cannot_be_sold?: boolean;
  online_store?: boolean;

  //Enabled Setting

  customerRequiredEnabled?: boolean;
  fulfillmentRequiredEnabled?: boolean;
  shippingRequiredEnabled?: boolean;
  trackInventoryEnabled?: boolean;
  cannotBeSoldEnabled?: boolean;
  onlineStoreEnabled?: boolean;
}

interface IFilterState {
  productTypeEnabled?: boolean;
  vendorEnabled?: boolean;
  accountingReferenceEnabled?: boolean;
  kitchenLocationEnabled?: boolean;
  kitchenLocation2Enabled?: boolean;
  kitchenLocation3Enabled?: boolean;
}

export default function DepartmentsEdit() {
  const history = useHistory();
  const { t } = useTranslation();
  const { Option } = Select;
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { authStore, facilityStore } = useAppSelector(store => store);
  const userCanEditProducts = authStore.user?.permissions?.products_edit;
  const facilityCanEditProducts = facilityStore.facility?.permissions?.products_edit;
  const [departmentState, setDepartmentState] = useState<IDepartmentState>({
    id: null,
    title: "",
    type: null,
    parent_id: null,

    productType: undefined,
    vendor: null,
    vendor_id: undefined,
    accounting_reference: null,
    accounting_reference_id: undefined,
    kitchen_location: null,
    kitchen_location_id: undefined,
    kitchen_location_id_2: undefined,
    kitchen_location_id_3: undefined,
    customer_required: false,
    fulfillment_required: false,
    shipping_required: false,
    track_inventory: false,
    cannot_be_sold: false,
    online_store: false,

    customerRequiredEnabled: false,
    fulfillmentRequiredEnabled: false,
    shippingRequiredEnabled: false,
    trackInventoryEnabled: false,
    cannotBeSoldEnabled: false,
    onlineStoreEnabled: false,
  });

  const [filterState, setFilterState] = useState<IFilterState>({
    productTypeEnabled: false,
    vendorEnabled: false,
    accountingReferenceEnabled: false,
    kitchenLocationEnabled: false,
    kitchenLocation2Enabled: false,
    kitchenLocation3Enabled: false,
  });

  const [allDepartState, setAllDepartState] = useState({
    department: [],
    dropDownValue: "Select New Parent Department",
    title: "",
  });
  const [deleteModalVisibility, setDeleteModalVisibility] = useState(false);

  const [departmentBeforeChanges, setdepartmentBeforeChanges] = useState(undefined);
  const [departmentLoaded, setdepartmentLoaded] = useState<boolean>(false);
  const [productTypesLoaded, setProductTypesLoaded] = useState<boolean>(false);
  const [vendorsLoaded, setVendorsLoaded] = useState<boolean>(false);
  const [accountingReferencesLoaded, setAccountingReferencesLoaded] = useState<boolean>(false);
  const [productTypes, setProductTypes] = useState<TProductType[]>([]);
  const [accountingReferences, setAccountingReferences] = useState<TAccountingReference[]>([]);
  const [vendors, setVendors] = useState<TProductVendor[]>([]);
  const [kitchenLocations, setKitchenLocations] = useState<TKitchenLocation[]>([]);

  const [logisticsCard, setLogisticsCard] = useState({
    productTypeSearch: null as string, //sets when product is returned.
    accountRefSearch: null as string,
    vendorSearch: null as string,
    kitchenLocationSearch: null as string,
    productUnitSearch: null as string,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();

    void loadDepartments();
    void loadProductTypes(source.token);
    void loadVendors(source.token);
    void loadAccountingRefereces(source.token);
    void loadKitchenLocations(source.token);

    return () => {
      setDepartmentState({ id: undefined, title: "", type: null, parent_id: null });
      setAllDepartState({ department: [], dropDownValue: "Select New Parent Department", title: "" });
    };
  }, []);

  // Keep Select search consistent with product type
  useEffect(() => {
    if (departmentState?.productType) {
      setLogisticsCard(prev => ({ ...prev, productTypeSearch: departmentState?.productType }));
    }
  }, [departmentState?.productType]);

  useEffect(() => {
    if (accountingReferences && departmentState?.accounting_reference !== undefined) {
      setLogisticsCard(prev => ({ ...prev, accountRefSearch: departmentState?.accounting_reference?.title }));
    }
  }, [accountingReferences, departmentState?.accounting_reference]);

  useEffect(() => {
    if (vendors && departmentState?.vendor_id) {
      const vendor = vendors.find(value => value.id === departmentState.vendor_id);

      setLogisticsCard(prev => ({ ...prev, vendorSearch: vendor ? vendor?.title : prev.vendorSearch }));
    }
  }, [vendors, departmentState?.vendor_id]);

  async function loadDepartments() {
    const res = await GetDepartments({ extended: true }, true);

    if (res.status !== StatusCode.OK) {
      return;
    }

    const currentDepartment = res.data.find((department: any) => department.id === Number(id));

    setAllDepartState(prev => ({
      ...prev,
      department: res.data,
      title: currentDepartment.title,
    }));

    setDepartmentState(prev => ({
      ...prev,
      id: currentDepartment.id,
      title: currentDepartment.title,
      type: currentDepartment.type,
      parent_id: currentDepartment.parent_id,
    }));

    setdepartmentLoaded(true);
  }

  async function loadVendors(token: CancelToken) {
    const res = await GetVendor(null, false, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      return;
    }

    setVendors(res.data);

    setVendorsLoaded(true);
  }

  async function loadProductTypes(token: CancelToken) {
    const res = await GetProductTypes(null, false, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      return;
    }

    setProductTypes(res.data);
    setProductTypesLoaded(true);
  }

  async function loadAccountingRefereces(token?: CancelToken) {
    const res = await GetAccountingReference(null, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      return;
    }

    setAccountingReferences(res.data);
    setAccountingReferencesLoaded(true);
  }

  async function loadKitchenLocations(token?: CancelToken) {
    if (kitchenLocations !== undefined) {
      setKitchenLocations(undefined);
    }

    const res = await GetKitchenLocations(null, token ? false : true, token);
    if (token && token.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.settings.departments.departments_edit.027")));
    }

    setKitchenLocations(res.status !== StatusCode.OK ? [] : res.data);
    // setKitchenLocationReload(false);
  }

  async function saveDepartment() {
    if (departmentState.title != String(departmentBeforeChanges.title)) {
      const res = await PutDepartments(departmentState, true);

      if (res.status !== StatusCode.OK) {
        dispatch(showError("Error saving department"));
        return;
      }
    }

    const params = {
      id: departmentState.id,
      type: filterState?.productTypeEnabled ? departmentState.productType : null,
      vendor_id: filterState?.vendorEnabled ? departmentState.vendor_id : null,
      vendor_title: filterState?.vendorEnabled ? departmentState.vendor.title : null,
      kitchen_location_id:
        filterState?.kitchenLocationEnabled && departmentState.kitchen_location_id !== -1
          ? departmentState.kitchen_location_id
          : null,
      kitchen_location_id_2:
        filterState?.kitchenLocation2Enabled && departmentState.kitchen_location_id_2 !== -1
          ? departmentState.kitchen_location_id_2
          : null,
      kitchen_location_id_3:
        filterState?.kitchenLocation3Enabled && departmentState.kitchen_location_id_3 !== -1
          ? departmentState.kitchen_location_id_3
          : null,
      accounting_reference_id: filterState?.accountingReferenceEnabled ? departmentState.accounting_reference_id : null,
      cannot_be_sold: departmentState?.cannotBeSoldEnabled ? departmentState.cannot_be_sold : null,
      fulfillment_required: departmentState?.fulfillmentRequiredEnabled ? departmentState.fulfillment_required : null,
      customer_required: departmentState?.customerRequiredEnabled ? departmentState.customer_required : null,
      shipping_required: departmentState?.shippingRequiredEnabled ? departmentState.shipping_required : null,
      track_inventory: departmentState?.trackInventoryEnabled ? departmentState.track_inventory : null,
      online_store: departmentState?.onlineStoreEnabled ? departmentState.online_store : null,
    };

    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === undefined) {
        delete params[key as keyof typeof params];
      }
    }

    if (Object.keys(params).length > 1) {
      const updateProductRes = await UpdateAllDepartmentProducts(params, true);

      if (updateProductRes.status !== StatusCode.OK) {
        dispatch(showError("Error updating all products in department"));
        return;
      }
    }

    history.push("/admin/settings/departments");
  }

  async function removeDepartment() {
    const res = await DeleteDepartments(departmentState, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError("Error removing department"));

      return;
    }

    history.push("/admin/settings/departments");
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = event.target;
    setDepartmentState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleDeleteModalVisibility() {
    setDeleteModalVisibility(!deleteModalVisibility);
  }

  function handleDropDownChange(value: any, property: string) {
    setDepartmentState(prev => ({
      ...prev,
      [property]: value,
    }));
  }

  const secondaryAction = {
    content: t("secure.facility.settings.departments.departments_edit.002"),
    action: handleDeleteModalVisibility,
  };

  function unsavedChangesExist() {
    if (departmentBeforeChanges === undefined) {
      if (departmentLoaded && vendorsLoaded && productTypesLoaded && accountingReferencesLoaded) {
        setdepartmentBeforeChanges(departmentState);
      }
      return false;
    }

    return !isEqualWith(departmentBeforeChanges, departmentState);
  }

  function cancelUnsavedChanges() {
    setDepartmentState(departmentBeforeChanges);
  }

  function logisticsCardSearch(
    key: keyof {
      productTypeSearch: string;
      accountRefSearch: string;
      vendorSearch: string;
      kitchenLocationSearch: string;
    },
    query: string,
  ) {
    // handle the search query
    setLogisticsCard(prev => ({ ...prev, [key]: query }));

    // If search is cleared, remove the product param
    if (logisticsCard[key] !== null && query?.length === 0) {
      switch (key) {
        case "accountRefSearch": {
          setDepartmentState(prev => ({ ...prev, accounting_reference_id: undefined }));
          break;
        }
        case "productTypeSearch": {
          setDepartmentState(prev => ({ ...prev, productType: undefined }));
          break;
        }
        case "vendorSearch": {
          setDepartmentState(prev => ({ ...prev, vendor_id: undefined }));
          break;
        }

        default:
          return;
      }
    }
  }

  function handleCheckboxChange(event: any) {
    const { id, checked } = event.target;
    setDepartmentState(prevState => ({ ...prevState, [id]: checked }));
  }

  function handleInputCheckboxChange(event: any) {
    const { id, checked } = event.target;
    setFilterState(prevState => ({ ...prevState, [id]: checked }));
  }

  return (
    <div>
      <Page
        title={t("secure.facility.settings.departments.departments_edit.003") + allDepartState.title}
        narrow
        secondaryActions={[secondaryAction]}
        breadcrumbs={[
          {
            prefix: true,
            label: t("secure.facility.settings.departments.departments_edit.004"),
            url: "/admin/settings/departments",
          },
        ]}
        notificationBarProps={{
          isVisible: unsavedChangesExist(),
          onAction: saveDepartment,
          onCancel: cancelUnsavedChanges,
        }}
      >
        <Card>
          <Card.Section>
            <Input
              value={departmentState.title}
              label={t("secure.facility.settings.departments.departments_edit.005")}
              id="title"
              onChange={handleInputChange}
              placeholder={t("secure.facility.settings.departments.departments_edit.006")}
            />
          </Card.Section>

          {departmentState.type !== "department" && (
            <Card.Section>
              <Select
                label={t("secure.facility.settings.departments.departments_edit.007")}
                onChange={(value: any) => handleDropDownChange(value, "parent_id")}
                defaultValue={departmentState.parent_id}
              >
                {allDepartState.department
                  ?.filter((dept: any) => dept.type === "department")
                  ?.map((department: any, index: any) => {
                    return departmentState.type === "category" ? (
                      <Option key={index} value={department.id} name={department.title}>
                        <span>{department.title}</span>
                      </Option>
                    ) : (
                      <React.Fragment key={index}>
                        <p style={{ padding: "8px 16px" }}>
                          <b>{department.title}</b>
                        </p>
                        {allDepartState.department
                          ?.filter((dept: any) => dept.parent_id === department.id)
                          .map((department: any, index: number) => {
                            return (
                              <Option key={index} value={department.id} name={department.title}>
                                {department.title}
                              </Option>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
              </Select>
            </Card.Section>
          )}

          {userCanEditProducts ? (
            <Card.Section title={t("secure.facility.settings.departments.departments_edit.014")}>
              <DataTable
                columns={[{ label: "" }, { label: "Setting Title" }, { label: "", width: "50%" }]}
                className="min-h-auto"
              >
                {facilityCanEditProducts && userCanEditProducts ? (
                  <tr>
                    <td>
                      <Checkbox
                        id={"productTypeEnabled"}
                        size="medium"
                        checked={filterState.productTypeEnabled}
                        onChange={handleInputCheckboxChange}
                      />
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() =>
                        setFilterState(prevState => ({
                          ...prevState,
                          productTypeEnabled: !filterState.productTypeEnabled,
                        }))
                      }
                    >
                      {t("secure.facility.settings.departments.departments_edit.015")}
                    </td>
                    <td
                      className={!filterState.productTypeEnabled ? "cursor-pointer" : ""}
                      onClick={() =>
                        !filterState.productTypeEnabled
                          ? setFilterState(prevState => ({
                              ...prevState,
                              productTypeEnabled: !filterState.productTypeEnabled,
                            }))
                          : {}
                      }
                    >
                      <Select
                        // label={t("secure.facility.settings.departments.departments_edit.015")}
                        placeholder={t("secure.facility.settings.departments.departments_edit.016")}
                        className={`flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200`}
                        onSearch={(query: string) => logisticsCardSearch("productTypeSearch", query)}
                        onChange={(value: string) => setDepartmentState(prev => ({ ...prev, productType: value }))}
                        searchValue={logisticsCard.productTypeSearch || ""}
                        showSearch={filterState.productTypeEnabled}
                        showDropDownOnFocus
                        hideDropdown
                        disabled={!filterState.productTypeEnabled}
                      >
                        {productTypes
                          .filter(val =>
                            val?.title?.toLowerCase().includes(logisticsCard.productTypeSearch?.toLowerCase()),
                          )
                          .map(type => {
                            return (
                              <Select.Option key={type.id} value={type.title} name={type.title}>
                                {type.title}
                              </Select.Option>
                            );
                          })}
                      </Select>
                    </td>
                  </tr>
                ) : null}
                {facilityCanEditProducts && userCanEditProducts ? (
                  <tr>
                    <td>
                      <Checkbox
                        id={"vendorEnabled"}
                        size="medium"
                        checked={filterState.vendorEnabled}
                        onChange={handleInputCheckboxChange}
                      />
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() =>
                        setFilterState(prevState => ({ ...prevState, vendorEnabled: !filterState.vendorEnabled }))
                      }
                    >
                      {t("secure.facility.settings.departments.departments_edit.017")}
                    </td>
                    <td
                      className={!filterState.vendorEnabled ? "cursor-pointer" : ""}
                      onClick={() =>
                        !filterState.vendorEnabled
                          ? setFilterState(prevState => ({
                              ...prevState,
                              vendorEnabled: !filterState.vendorEnabled,
                            }))
                          : {}
                      }
                    >
                      <Select
                        onChange={(value: number, vendor: TProductVendor) =>
                          setDepartmentState(prev => ({ ...prev, vendor_id: value, vendor: vendor }))
                        }
                        className={`flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200`}
                        onSearch={(query: string) => logisticsCardSearch("vendorSearch", query)}
                        placeholder={t("secure.facility.settings.departments.departments_edit.018")}
                        searchValue={logisticsCard.vendorSearch || ""}
                        showSearch={filterState.vendorEnabled}
                        showDropDownOnFocus
                        hideDropdown
                        disabled={!filterState.vendorEnabled}
                      >
                        {vendors
                          .filter(val => val.title?.toLowerCase().includes(logisticsCard.vendorSearch?.toLowerCase()))
                          .map((vendor, index: number) => {
                            return (
                              <Select.Option key={index} value={vendor.id} name={vendor.title} extraValues={vendor}>
                                {vendor.title}
                              </Select.Option>
                            );
                          })}
                      </Select>
                    </td>
                  </tr>
                ) : null}
                {facilityCanEditProducts && userCanEditProducts ? (
                  <tr>
                    <td>
                      <Checkbox
                        id={"accountingReferenceEnabled"}
                        size="medium"
                        checked={filterState.accountingReferenceEnabled}
                        onChange={handleInputCheckboxChange}
                      />
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() =>
                        setFilterState(prevState => ({
                          ...prevState,
                          accountingReferenceEnabled: !filterState.accountingReferenceEnabled,
                        }))
                      }
                    >
                      {t("secure.facility.settings.departments.departments_edit.019")}
                    </td>
                    <td
                      className={!filterState.accountingReferenceEnabled ? "cursor-pointer" : ""}
                      onClick={() =>
                        !filterState.accountingReferenceEnabled
                          ? setFilterState(prevState => ({
                              ...prevState,
                              accountingReferenceEnabled: !filterState.accountingReferenceEnabled,
                            }))
                          : {}
                      }
                    >
                      <Select
                        // label={t("secure.facility.settings.departments.departments_edit.019")}
                        placeholder={t("secure.facility.settings.departments.departments_edit.020")}
                        className={`flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200`}
                        onSearch={(query: string) => logisticsCardSearch("accountRefSearch", query)}
                        onChange={(value: any) =>
                          setDepartmentState(prev => ({ ...prev, accounting_reference_id: value }))
                        }
                        searchValue={logisticsCard.accountRefSearch || ""}
                        showDropDownOnFocus={false}
                        hideDropdown
                        showSearch={filterState.accountingReferenceEnabled}
                        disabled={!filterState.accountingReferenceEnabled}
                      >
                        {/* <div key={"wrap"}> */}
                        {accountingReferences
                          .filter(accountingReference =>
                            accountingReference.title
                              ?.toLowerCase()
                              .includes(logisticsCard.accountRefSearch?.toLowerCase()),
                          )
                          .map(reference => {
                            return (
                              <Select.Option key={reference.id} value={reference.id} name={reference.title}>
                                {reference.title}
                              </Select.Option>
                            );
                          })}
                        {/* </div> */}
                      </Select>
                    </td>
                  </tr>
                ) : null}
                {userCanEditProducts ? (
                  <tr>
                    <td>
                      <Checkbox
                        id={"kitchenLocationEnabled"}
                        size="medium"
                        checked={filterState.kitchenLocationEnabled}
                        onChange={handleInputCheckboxChange}
                      />
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() =>
                        setFilterState(prevState => ({
                          ...prevState,
                          kitchenLocationEnabled: !filterState.kitchenLocationEnabled,
                        }))
                      }
                    >
                      {"Kitchen Location"}
                    </td>
                    <td
                      className={!filterState.kitchenLocationEnabled ? "cursor-pointer" : ""}
                      onClick={() =>
                        !filterState.kitchenLocationEnabled
                          ? setFilterState(prevState => ({
                              ...prevState,
                              kitchenLocationEnabled: !filterState.kitchenLocationEnabled,
                            }))
                          : {}
                      }
                    >
                      <Select
                        label=""
                        onChange={(id: any) =>
                          setDepartmentState(prevState => ({ ...prevState, kitchen_location_id: id }))
                        }
                        defaultValue={departmentState.kitchen_location_id}
                        disabled={!filterState.kitchenLocationEnabled}
                      >
                        <Select.Option key={"none"} value={-1} name="none">
                          {t("secure.facility.product.product.135")}
                        </Select.Option>
                        {kitchenLocations
                          ?.filter((location: any) => {
                            // Filter out other selected kitchen_location_ids
                            if (
                              location.id !== departmentState.kitchen_location_id_2 &&
                              location.id !== departmentState.kitchen_location_id_3
                            ) {
                              return location;
                            }
                          })
                          .map((location, index: number) => {
                            return (
                              <Select.Option key={index} value={location.id} name={location.title}>
                                {location.title}
                              </Select.Option>
                            );
                          })}
                      </Select>
                    </td>
                  </tr>
                ) : null}
                {userCanEditProducts ? (
                  <tr>
                    <td>
                      <Checkbox
                        id={"kitchenLocation2Enabled"}
                        size="medium"
                        checked={filterState.kitchenLocation2Enabled}
                        onChange={handleInputCheckboxChange}
                      />
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() =>
                        setFilterState(prevState => ({
                          ...prevState,
                          kitchenLocation2Enabled: !filterState.kitchenLocation2Enabled,
                        }))
                      }
                    >
                      {"Kitchen Location 2"}
                    </td>
                    <td
                      className={!filterState.kitchenLocation2Enabled ? "cursor-pointer" : ""}
                      onClick={() =>
                        !filterState.kitchenLocation2Enabled
                          ? setFilterState(prevState => ({
                              ...prevState,
                              kitchenLocation2Enabled: !filterState.kitchenLocation2Enabled,
                            }))
                          : {}
                      }
                    >
                      <Select
                        label=""
                        onChange={(id: any) =>
                          setDepartmentState(prevState => ({ ...prevState, kitchen_location_id_2: id }))
                        }
                        defaultValue={departmentState.kitchen_location_id_2}
                        disabled={!filterState.kitchenLocation2Enabled}
                      >
                        <Select.Option key={"none"} value={-1} name="none">
                          {t("secure.facility.product.product.135")}
                        </Select.Option>
                        {kitchenLocations
                          ?.filter((location: any) => {
                            // Filter out other selected kitchen_location_ids
                            if (
                              location.id !== departmentState.kitchen_location_id &&
                              location.id !== departmentState.kitchen_location_id_3
                            ) {
                              return location;
                            }
                          })
                          .map((location, index: number) => {
                            return (
                              <Select.Option key={index} value={location.id} name={location.title}>
                                {location.title}
                              </Select.Option>
                            );
                          })}
                      </Select>
                    </td>
                  </tr>
                ) : null}
                {userCanEditProducts ? (
                  <tr>
                    <td>
                      <Checkbox
                        id={"kitchenLocation3Enabled"}
                        size="medium"
                        checked={filterState.kitchenLocation3Enabled}
                        onChange={handleInputCheckboxChange}
                      />
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() =>
                        setFilterState(prevState => ({
                          ...prevState,
                          kitchenLocation3Enabled: !filterState.kitchenLocation3Enabled,
                        }))
                      }
                    >
                      {"Kitchen Location 3"}
                    </td>
                    <td
                      className={!filterState.kitchenLocation3Enabled ? "cursor-pointer" : ""}
                      onClick={() =>
                        !filterState.kitchenLocation3Enabled
                          ? setFilterState(prevState => ({
                              ...prevState,
                              kitchenLocation3Enabled: !filterState.kitchenLocation3Enabled,
                            }))
                          : {}
                      }
                    >
                      <Select
                        label=""
                        onChange={(id: any) =>
                          setDepartmentState(prevState => ({ ...prevState, kitchen_location_id_3: id }))
                        }
                        defaultValue={departmentState.kitchen_location_id_3}
                        disabled={!filterState.kitchenLocation3Enabled}
                      >
                        <Select.Option key={"none"} value={-1} name="none">
                          {t("secure.facility.product.product.135")}
                        </Select.Option>
                        {kitchenLocations
                          ?.filter((location: any) => {
                            // Filter out other selected kitchen_location_ids
                            if (
                              location.id !== departmentState.kitchen_location_id_2 &&
                              location.id !== departmentState.kitchen_location_id
                            ) {
                              return location;
                            }
                          })
                          .map((location, index: number) => {
                            return (
                              <Select.Option key={index} value={location.id} name={location.title}>
                                {location.title}
                              </Select.Option>
                            );
                          })}
                      </Select>
                    </td>
                  </tr>
                ) : null}
                {facilityCanEditProducts && userCanEditProducts ? (
                  <tr>
                    <td>
                      <Checkbox
                        id={"customerRequiredEnabled"}
                        size="medium"
                        checked={departmentState.customerRequiredEnabled}
                        onChange={handleCheckboxChange}
                      />
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() =>
                        setDepartmentState(prevState => ({
                          ...prevState,
                          customerRequiredEnabled: !departmentState.customerRequiredEnabled,
                        }))
                      }
                    >
                      {t("secure.facility.settings.departments.departments_edit.021")}
                    </td>
                    <td
                      className={!departmentState.customerRequiredEnabled ? "cursor-pointer" : ""}
                      onClick={() =>
                        !departmentState.customerRequiredEnabled
                          ? setDepartmentState(prevState => ({
                              ...prevState,
                              customerRequiredEnabled: !departmentState.customerRequiredEnabled,
                            }))
                          : {}
                      }
                    >
                      <Toggle
                        id={"customer_required"}
                        onChange={() =>
                          setDepartmentState(prevState => ({
                            ...prevState,
                            customer_required: !departmentState.customer_required,
                          }))
                        }
                        checked={departmentState?.customer_required}
                        disabled={!departmentState.customerRequiredEnabled}
                      />
                    </td>
                  </tr>
                ) : null}
                {facilityCanEditProducts && userCanEditProducts ? (
                  <tr>
                    <td>
                      <Checkbox
                        id={"shippingRequiredEnabled"}
                        size="medium"
                        checked={departmentState.shippingRequiredEnabled}
                        onChange={handleCheckboxChange}
                      />
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() =>
                        setDepartmentState(prevState => ({
                          ...prevState,
                          shippingRequiredEnabled: !departmentState.shippingRequiredEnabled,
                        }))
                      }
                    >
                      {t("secure.facility.settings.departments.departments_edit.024")}
                    </td>
                    <td
                      className={!departmentState.shippingRequiredEnabled ? "cursor-pointer" : ""}
                      onClick={() =>
                        !departmentState.shippingRequiredEnabled
                          ? setDepartmentState(prevState => ({
                              ...prevState,
                              shippingRequiredEnabled: !departmentState.shippingRequiredEnabled,
                            }))
                          : {}
                      }
                    >
                      <Toggle
                        id={"shipping_required"}
                        onChange={() =>
                          setDepartmentState(prevState => ({
                            ...prevState,
                            shipping_required: !departmentState.shipping_required,
                          }))
                        }
                        checked={departmentState?.shipping_required}
                        disabled={!departmentState.shippingRequiredEnabled}
                      />
                    </td>
                  </tr>
                ) : null}
                {facilityCanEditProducts && userCanEditProducts ? (
                  <tr>
                    <td>
                      <Checkbox
                        id={"fulfillmentRequiredEnabled"}
                        size="medium"
                        checked={departmentState.fulfillmentRequiredEnabled}
                        onChange={handleCheckboxChange}
                      />
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() =>
                        setDepartmentState(prevState => ({
                          ...prevState,
                          fulfillmentRequiredEnabled: !departmentState.fulfillmentRequiredEnabled,
                        }))
                      }
                    >
                      {t("secure.facility.settings.departments.departments_edit.023")}
                    </td>
                    <td
                      className={!departmentState.fulfillmentRequiredEnabled ? "cursor-pointer" : ""}
                      onClick={() =>
                        !departmentState.fulfillmentRequiredEnabled
                          ? setDepartmentState(prevState => ({
                              ...prevState,
                              fulfillmentRequiredEnabled: !departmentState.fulfillmentRequiredEnabled,
                            }))
                          : {}
                      }
                    >
                      <Toggle
                        id={"fulfillment_required"}
                        onChange={() =>
                          setDepartmentState(prevState => ({
                            ...prevState,
                            fulfillment_required: !departmentState.fulfillment_required,
                          }))
                        }
                        checked={departmentState?.fulfillment_required}
                        disabled={!departmentState.fulfillmentRequiredEnabled}
                      />
                    </td>
                  </tr>
                ) : null}
                {userCanEditProducts ? (
                  <tr>
                    <td>
                      <Checkbox
                        id={"cannotBeSoldEnabled"}
                        size="medium"
                        checked={departmentState.cannotBeSoldEnabled}
                        onChange={handleCheckboxChange}
                      />
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() =>
                        setDepartmentState(prevState => ({
                          ...prevState,
                          cannotBeSoldEnabled: !departmentState.cannotBeSoldEnabled,
                        }))
                      }
                    >
                      {t("secure.facility.settings.departments.departments_edit.022")}
                    </td>
                    <td
                      className={!departmentState.cannotBeSoldEnabled ? "cursor-pointer" : ""}
                      onClick={() =>
                        !departmentState.cannotBeSoldEnabled
                          ? setDepartmentState(prevState => ({
                              ...prevState,
                              cannotBeSoldEnabled: !departmentState.cannotBeSoldEnabled,
                            }))
                          : {}
                      }
                    >
                      <Toggle
                        id={"cannot_be_sold"}
                        onChange={() =>
                          setDepartmentState(prevState => ({
                            ...prevState,
                            cannot_be_sold: !departmentState.cannot_be_sold,
                          }))
                        }
                        checked={departmentState?.cannot_be_sold}
                        disabled={!departmentState.cannotBeSoldEnabled}
                      />
                    </td>
                  </tr>
                ) : null}
                {facilityCanEditProducts && userCanEditProducts ? (
                  <tr>
                    <td>
                      <Checkbox
                        id={"trackInventoryEnabled"}
                        size="medium"
                        checked={departmentState.trackInventoryEnabled}
                        onChange={handleCheckboxChange}
                      />
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() =>
                        setDepartmentState(prevState => ({
                          ...prevState,
                          trackInventoryEnabled: !departmentState.trackInventoryEnabled,
                        }))
                      }
                    >
                      {t("secure.facility.settings.departments.departments_edit.025")}
                    </td>
                    <td
                      className={!departmentState.trackInventoryEnabled ? "cursor-pointer" : ""}
                      onClick={() =>
                        !departmentState.trackInventoryEnabled
                          ? setDepartmentState(prevState => ({
                              ...prevState,
                              trackInventoryEnabled: !departmentState.trackInventoryEnabled,
                            }))
                          : {}
                      }
                    >
                      <Toggle
                        id={"track_inventory"}
                        onChange={() =>
                          setDepartmentState(prevState => ({
                            ...prevState,
                            track_inventory: !departmentState.track_inventory,
                          }))
                        }
                        checked={departmentState?.track_inventory}
                        disabled={!departmentState.trackInventoryEnabled}
                      />
                    </td>
                  </tr>
                ) : null}
                {userCanEditProducts ? (
                  <tr>
                    <td>
                      <Checkbox
                        id={"onlineStoreEnabled"}
                        size="medium"
                        checked={departmentState.onlineStoreEnabled}
                        onChange={handleCheckboxChange}
                      />
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() =>
                        setDepartmentState(prevState => ({
                          ...prevState,
                          onlineStoreEnabled: !departmentState.onlineStoreEnabled,
                        }))
                      }
                    >
                      {t("secure.facility.settings.departments.departments_edit.026")}
                    </td>
                    <td
                      className={!departmentState.onlineStoreEnabled ? "cursor-pointer" : ""}
                      onClick={() =>
                        !departmentState.onlineStoreEnabled
                          ? setDepartmentState(prevState => ({
                              ...prevState,
                              onlineStoreEnabled: !departmentState.onlineStoreEnabled,
                            }))
                          : {}
                      }
                    >
                      <Toggle
                        id={"online_store"}
                        onChange={() =>
                          setDepartmentState(prevState => ({
                            ...prevState,
                            online_store: !departmentState.online_store,
                          }))
                        }
                        checked={departmentState?.online_store}
                        disabled={!departmentState.onlineStoreEnabled}
                      />
                    </td>
                  </tr>
                ) : null}
              </DataTable>
            </Card.Section>
          ) : null}
        </Card>
      </Page>

      <Popup
        open={deleteModalVisibility}
        type="warning"
        title={t("secure.facility.settings.departments.departments_edit.010")}
        description={
          t("secure.facility.settings.departments.departments_edit.011") +
          " categories, subcategories and " +
          t("secure.facility.settings.departments.departments_edit.012") +
          "\n" +
          t("secure.facility.settings.departments.departments_edit.013")
        }
        onOk={removeDepartment}
        okText={t("secure.facility.settings.departments.departments_edit.009")}
        onCancel={handleDeleteModalVisibility}
        backDropCancel={true}
      />
    </div>
  );
}
